import {animate, state, style, transition, trigger} from "@angular/animations";
import {CDK_DRAG_CONFIG, CDK_DROP_LIST, CdkDrag, CdkDragHandle, DragRef, Point} from "@angular/cdk/drag-drop";
import {CdkOverlayOrigin, ComponentType, ConnectedPosition, Overlay, OverlayConfig as CdkOverlayConfig, OverlayRef, OverlaySizeConfig, PositionStrategy, ScrollStrategy, OverlayContainer} from "@angular/cdk/overlay";
import {CdkPortal, CdkPortalOutlet, ComponentPortal, Portal, TemplatePortal} from "@angular/cdk/portal";
import {CommonModule} from "@angular/common";
import {Component, ComponentRef, ContentChild, ElementRef, EventEmitter, Injector, Input, OnInit, Output, TemplateRef, Type, ViewChild, ViewContainerRef, ChangeDetectionStrategy} from "@angular/core";
import {NavigationStart, Router} from "@angular/router";

import {CdkModule} from "framework/cdk";
import {MaterialModule} from "framework/material";
import {UtilsService} from "framework/services/utils.service";
import {IconButtonComponent} from "framework/ui/buttons/icon-button.component";
import {BaseComponent, fromEvent} from "framework/ui/components";
import {CsTestId} from "framework/ui/directives/optio3-test-id/optio3-test-id.directive";
import {OVERLAY_DATA, OverlayDialogConfig, OverlayDialogRef} from "framework/ui/overlays/overlay-dialog";
import {Future} from "framework/utils/concurrency";

@Component({
               selector   : "o3-overlay",
               styleUrls  : ["./overlay.component.scss"],
               templateUrl: "./overlay.component.html",
               providers  : [
                   {
                       provide : CDK_DRAG_CONFIG,
                       useValue: {
                           dragStartThreshold             : 0,
                           pointerDirectionChangeThreshold: 5
                       }
                   },
                   {
                       // when o3-overlay is descendant of a cdkDropList it becomes connected and breaks overlay dragging
                       provide : CDK_DROP_LIST,
                       useValue: null
                   }
               ],
               animations : [
                   trigger("transformDropdown", [
                       state("void", style({
                                               transform: "scaleY(0)",
                                               minWidth : "100%",
                                               opacity  : 0
                                           })),
                       state("enter", style({
                                                opacity  : 1,
                                                minWidth : "100%",
                                                transform: "scaleY(1)"
                                            })),
                       state("leave", style({
                                                transform: "scaleY(0)",
                                                minWidth : "100%",
                                                opacity  : 0
                                            })),
                       transition("* => enter", animate(`${OverlayComponent.animationDuration}ms ease-out`)),
                       transition("enter => *", animate(`${OverlayComponent.animationDuration}ms ease-in`))
                   ]),
                   trigger("transformDialog", [
                       state("void", style({
                                               transform: "{{ voidTransform }}",
                                               opacity  : 0
                                           }), {params: {voidTransform: "translateY(25%) scale(0.9)"}}),
                       state("enter", style({
                                                transform: "translateY(0) scale(1)",
                                                opacity  : 1
                                            })),
                       state("leave", style({
                                                transform: "{{ leaveTransform }}",
                                                opacity  : 0
                                            }), {params: {leaveTransform: "translateY(25%)"}}),
                       transition("* => enter", animate(`${OverlayComponent.animationDuration}ms ease-out`)),
                       transition("enter => *", animate(`${OverlayComponent.animationDuration}ms ease-in`))
                   ])
               ],
               changeDetection: ChangeDetectionStrategy.Eager,
               imports    : [
                   CdkModule,
                   CommonModule,
                   MaterialModule,
                   IconButtonComponent,
                   CsTestId
               ]
           })
export class OverlayComponent extends BaseComponent implements OnInit
{
    static readonly animationDuration = 100;
    static readonly gripperHeight     = 16;
    static readonly gripperWidth      = 32;

    private m_onTopMaintainer: MutationObserver;
    private static numTopLevelOverlays = 0;
    private m_cdkConfig: CdkOverlayConfig;
    private get cdkConfig(): CdkOverlayConfig
    {
        if (!this.m_cdkConfig) this.buildConfig();

        return this.m_cdkConfig;
    }

    private m_config: OverlayConfig;
    @Input() set config(config: OverlayConfig)
    {
        this.m_config = config;
        this.buildConfig();
    }

    get config(): OverlayConfig
    {
        return this.m_config;
    }

    private m_overlayOrigin: CdkOverlayOrigin;
    @Input() set overlayOrigin(origin: CdkOverlayOrigin)
    {
        this.m_overlayOrigin = origin;
        this.buildConfig();
    }

    get overlayOrigin(): CdkOverlayOrigin
    {
        return this.m_overlayOrigin;
    }

    @Output() opened = new EventEmitter<void>();
    @Output() closed = new EventEmitter<void>();

    @ViewChild(TemplateRef, {static: true}) templateRef: TemplateRef<any>;
    @ViewChild(CdkDrag) overlayElement: CdkDrag;
    @ViewChild(CdkDragHandle, {read: ElementRef}) handle: ElementRef;
    @ViewChild("test_close", {read: ElementRef}) test_close: ElementRef;

    @ContentChild(CdkPortal, {static: true}) set contentPortal(portal: CdkPortal)
    {
        this.portal = portal;
    }

    @ViewChild(CdkPortalOutlet) portalOutlet: CdkPortalOutlet;

    portal: Portal<any>;

    overlay: OverlayRef;

    animationState: "void" | "enter" | "leave" = "void";

    overlayId: string;

    trapFocus = false;

    private m_isDialog: boolean;

    private m_portal: TemplatePortal;

    get isOpen(): boolean
    {
        return this.overlay?.hasAttached();
    }

    get isOpening(): boolean
    {
        return !!this.overlay;
    }

    get dialogVoidTransform(): string
    {
        return this.config.slideFromRight ? "translateX(100%)" : `translateY(${this.config.dialogAnimationTranslateY}) scale(0.9)`;
    }

    get dialogLeaveTransform(): string
    {
        return this.config.slideFromRight ? "translateX(100%)" : "translateY(25%)";
    }

    private dragOffsetX: number;
    private dragOffsetY: number;
    dragConstrainFn = (point: Point,
                       dragRef: DragRef,
                       dimensions: ClientRect,
                       pickupPositionInElement: Point) =>
    {
        point.x = UtilsService.clamp(this.dragOffsetX, window.innerWidth - (OverlayComponent.gripperWidth - this.dragOffsetX), point.x);
        point.y = UtilsService.clamp(this.dragOffsetY, window.innerHeight - (OverlayComponent.gripperHeight - this.dragOffsetY), point.y);

        point.x -= pickupPositionInElement.x;
        point.y -= pickupPositionInElement.y;

        return point;
    };

    dragPosition = {
        x: 0,
        y: 0
    };

    constructor(private viewContainerRef: ViewContainerRef,
                private overlayService: Overlay,
                private overlayContainer: OverlayContainer,
                inj: Injector)
    {
        super(inj);
    }

    public static openRef<T, R>(inj: Injector,
                                componentRef: ComponentType<any>,
                                config: OverlayDialogConfig<T>): OverlayDialogRef<R>
    {
        if (!config.config) config.config = new OverlayConfig();

        let overlayComponent = OverlayComponent.createOverlayComponent(inj, config);

        let overlayDialogRef = new OverlayDialogRef<R>(overlayComponent);
        overlayComponent.attachPortal(config, overlayDialogRef, componentRef);

        return overlayDialogRef;
    }

    public static open<T, R>(base: BaseComponent,
                             componentRef: ComponentType<any>,
                             config: OverlayDialogConfig<T>): Promise<R>
    {
        const overlayDialogRef = this.openRef<T, R>(base.injector, componentRef, config);
        base.registerOverlay(overlayDialogRef);
        return this.handleClose(base, overlayDialogRef);
    }

    private static handleClose<R>(base: BaseComponent,
                                  ref: OverlayDialogRef<any>): Promise<R>
    {
        const closed = new Future<R>();
        ref.afterClose()
           .subscribe((value) => closed.resolve(value));

        const router           = base.injector.get(Router);
        const navigatedAwaySub = router.events.subscribe((val) =>
                                                         {
                                                             if (val instanceof NavigationStart)
                                                             {
                                                                 ref.close(null);
                                                             }
                                                         });
        closed.then(() =>
                    {
                        navigatedAwaySub.unsubscribe();
                        base.overlayClosed(ref);
                    });

        return closed;
    }

    private static s_dialogs = new Set<OverlayComponent>();

    public static findDialogComponent<T>(type: ComponentType<T>): T
    {
        return this.findDialogComponentsHelper(type, false)[0];
    }

    public static findDialogComponents<T>(type: ComponentType<T>): T[]
    {
        return this.findDialogComponentsHelper(type, true);
    }

    private static findDialogComponentsHelper<T>(type: ComponentType<T>,
                                                 onlyFirst: boolean): T[]
    {
        let components: T[] = [];
        for (let comp of OverlayComponent.s_dialogs)
        {
            if (comp?.portalOutlet?.attachedRef instanceof ComponentRef)
            {
                const instance = comp.portalOutlet.attachedRef.instance;
                if (instance instanceof type)
                {
                    components.push(instance);
                    if (onlyFirst) return components;
                }
            }
        }

        return components;
    }

    private static createOverlayComponent(injector: Injector,
                                          config: OverlayDialogConfig<any>): OverlayComponent
    {
        let overlay: Overlay = injector.get(Overlay);

        let overlayPortal    = new ComponentPortal(OverlayComponent, null, injector);
        let overlayRef       = overlay.create();
        let overlayComponent = overlayRef.attach(overlayPortal).instance;
        OverlayComponent.s_dialogs.add(overlayComponent);

        overlayComponent.config     = config.config;
        overlayComponent.m_isDialog = true;
        overlayComponent.closed.subscribe(() =>
                                          {
                                              OverlayComponent.s_dialogs.delete(overlayComponent);
                                              overlayRef.detach();
                                          });

        return overlayComponent;
    }

    private attachPortal(config: OverlayDialogConfig<any>,
                         overlayDialogRef: OverlayDialogRef<any>,
                         componentRef: Type<any>): void
    {
        let componentInjector = Injector.create({
                                                    providers: [
                                                        {
                                                            provide : OVERLAY_DATA,
                                                            useValue: config.data
                                                        },
                                                        {
                                                            provide : OverlayDialogRef,
                                                            useValue: overlayDialogRef
                                                        },
                                                        {
                                                            provide : OverlayComponent,
                                                            useValue: this
                                                        }
                                                    ],
                                                    parent   : this.injector
                                                });

        this.portal = new ComponentPortal(componentRef, null, componentInjector);
    }

    ngOnInit()
    {
        super.ngOnInit();

        this.m_portal = new TemplatePortal(this.templateRef, this.viewContainerRef);

        if (this.m_isDialog)
        {
            this.toggleOverlay();
        }
    }

    protected afterLayoutChange(): void
    {
        super.afterLayoutChange();

        this.pullIntoView();
    }

    ngOnDestroy()
    {
        super.ngOnDestroy();
        this.destroyOverlay();
    }

    onAnimationDone()
    {
        switch (this.animationState)
        {
            case "leave":
                this.detachOverlay();
                break;

            case "enter":
                if (this.m_isDialog) this.reportLayoutChange();
                break;
        }
    }

    toggleOverlay(overlayOrigin: CdkOverlayOrigin = null)
    {
        if (!this.overlay) this.createOverlay();

        // reset the origin if one is provided
        if (overlayOrigin)
        {
            this.overlayOrigin = overlayOrigin;
            this.buildConfig();
            this.overlay.updatePositionStrategy(this.cdkConfig.positionStrategy);
        }

        if (!this.overlay.hasAttached())
        {
            this.attachOverlay();
        }
        else
        {
            this.detachOverlay();
        }
    }

    closeOverlay()
    {
        if (!this.overlay) return;
        this.detachOverlay();
    }

    setDragConstraints(event: MouseEvent)
    {
        let handleRect   = this.handle.nativeElement.getBoundingClientRect();
        this.dragOffsetX = event.clientX - handleRect.left;
        this.dragOffsetY = event.clientY - handleRect.top;
    }

    resetPosition()
    {
        this.overlayElement?.reset();
    }

    resize(sizeConfig: OverlaySizeConfig): void
    {
        if (this.overlay)
        {
            this.overlay.updateSize(sizeConfig);
        }
    }

    pullIntoView(timeoutDelay?: number)
    {
        if (this.isOpen && this.handle)
        {
            if (isNaN(timeoutDelay))
            {
                this.pullIntoViewHelper();
            }
            else
            {
                setTimeout(() => this.pullIntoViewHelper(), timeoutDelay);
            }
        }
    }

    private pullIntoViewHelper()
    {
        let handleRect = this.handle.nativeElement.getBoundingClientRect();

        let needsAdjustment = false;
        let currentFreeDrag = this.overlayElement.getFreeDragPosition();
        let position        = {
            x: currentFreeDrag.x,
            y: currentFreeDrag.y
        };

        if (handleRect.left < 0)
        {
            needsAdjustment = true;
            position.x      = -(window.innerWidth - OverlayComponent.gripperWidth) / 2;
        }
        else if (handleRect.right > window.innerWidth)
        {
            needsAdjustment = true;
            position.x      = (window.innerWidth - OverlayComponent.gripperWidth) / 2;
        }

        let overlayHeight = this.overlayElement.element.nativeElement.clientHeight;
        if (handleRect.top < 0)
        {
            needsAdjustment = true;
            position.y      = (overlayHeight + OverlayComponent.gripperHeight - window.innerHeight) / 2;
        }
        else if (handleRect.bottom > window.innerHeight)
        {
            needsAdjustment = true;
            position.y      = (window.innerHeight + overlayHeight - OverlayComponent.gripperHeight) / 2;
        }

        if (needsAdjustment)
        {
            this.dragPosition = position;
            this.markForCheck();
        }
    }

    private buildConfig()
    {
        if (!this.m_config)
        {
            this.m_config = new OverlayConfig();
            if (this.overlayOrigin) this.m_config.dropdownDefaults();
        }

        if (this.overlayOrigin)
        {
            this.m_config.positionStrategy = this.overlayService.position()
                                                 .flexibleConnectedTo(this.overlayOrigin.elementRef)
                                                 .withPositions(this.getDropdownPositions())
                                                 .withPush(true)
                                                 .withGrowAfterOpen(true)
                                                 .withDefaultOffsetX(this.m_config.originOffsetX || 0)
                                                 .withDefaultOffsetY(this.m_config.originOffsetY || 0)
                                                 .withTransformOriginOn(".o3-overlay--container");
        }
        else if (!this.m_config.positionStrategy)
        {
            this.m_config.positionStrategy = this.overlayService.position()
                                                 .global()
                                                 .centerHorizontally()
                                                 .centerVertically();
        }

        let cdkConfig = new CdkOverlayConfig({
                                                 panelClass      : this.m_config.panelClass || "",
                                                 hasBackdrop     : this.m_config.showBackdrop,
                                                 width           : this.m_config.width,
                                                 minWidth        : this.m_config.minWidth,
                                                 maxWidth        : this.m_config.maxWidth,
                                                 height          : this.m_config.height,
                                                 minHeight       : this.m_config.minHeight,
                                                 maxHeight       : this.m_config.maxHeight,
                                                 positionStrategy: this.m_config.positionStrategy,
                                                 scrollStrategy  : this.m_config.scrollStrategy
                                             });

        if (this.m_config.isDraggable)
        {
            cdkConfig.hasBackdrop    = false;
            cdkConfig.scrollStrategy = this.overlayService.scrollStrategies.noop();
        }
        else if (!cdkConfig.scrollStrategy)
        {
            cdkConfig.scrollStrategy = this.overlayService.scrollStrategies.block();
        }

        cdkConfig.backdropClass = this.m_config.backdropClass || "cdk-overlay-dark-backdrop";

        this.m_cdkConfig = cdkConfig;
    }

    private getDropdownPositions(): ConnectedPosition[]
    {
        if (this.m_config.anchorPositions) return this.m_config.anchorPositions;

        if (this.m_config.coverAnchorWhenDisplayed)
        {
            return [
                // Overlay opens over the anchor
                {
                    originX : "start",
                    originY : "top",
                    overlayX: "start",
                    overlayY: "top"
                },
                {
                    originX : "start",
                    originY : "bottom",
                    overlayX: "start",
                    overlayY: "bottom"
                },
                {
                    originX : "end",
                    originY : "bottom",
                    overlayX: "end",
                    overlayY: "top"
                },
                {
                    originX : "end",
                    originY : "top",
                    overlayX: "end",
                    overlayY: "bottom"
                }
            ];
        }
        else
        {
            return [
                // Overlay opens under the anchor
                {
                    originX : "start",
                    originY : "bottom",
                    overlayX: "start",
                    overlayY: "top"
                },
                {
                    originX : "start",
                    originY : "top",
                    overlayX: "start",
                    overlayY: "bottom"
                },
                {
                    originX : "end",
                    originY : "bottom",
                    overlayX: "end",
                    overlayY: "top"
                },
                {
                    originX : "end",
                    originY : "top",
                    overlayX: "end",
                    overlayY: "bottom"
                }
            ];
        }
    }

    private getOverlayId(): string
    {
        if (this.overlay?.overlayElement?.id) return "#" + this.overlay.overlayElement.id;

        return "";
    }

    private updateSize()
    {
        // Overlay will default to the optimal width for its content.
        let width: string | number;
        let minWidth: string | number = 0;

        if (this.m_config.width) width = this.m_config.width;
        if (this.m_config.minWidth) minWidth = this.m_config.minWidth;

        if (this.overlayOrigin && this.overlayOrigin.elementRef && !minWidth && !width)
        {
            let rect = this.overlayOrigin.elementRef.nativeElement.getBoundingClientRect();
            if (rect && rect.width > 0)
            {
                minWidth = rect.width;
            }
        }

        this.overlay.updateSize({
                                    width   : width,
                                    minWidth: minWidth
                                });
    }

    private attachOverlay()
    {
        if (this.config?.showOnTop && !this.m_onTopMaintainer)
        {
            OverlayComponent.numTopLevelOverlays++;
            
            if (OverlayComponent.numTopLevelOverlays > 1)
            {
                console.error("More than one toplevel overlay detected.");
            }

            const hostEl = this.overlay.hostElement;
            if (hostEl?.showPopover)
            {
                const overlayContainer = this.overlayContainer.getContainerElement();
                if (overlayContainer)
                {
                    this.m_onTopMaintainer = new MutationObserver(() =>
                                                                  {
                                                                      try
                                                                      {
                                                                          hostEl.hidePopover?.();
                                                                          hostEl.showPopover?.();
                                                                      }
                                                                      catch
                                                                      {
                                                                          /* ignore if already hidden/shown */
                                                                      }
                                                                  });
                    this.m_onTopMaintainer.observe(overlayContainer, {childList: true});
                }
            }
        }

        if (this.overlayOrigin)
        {
            this.updateSize();
        }

        this.overlay.attach(this.m_portal);

        this.animationState = "enter";

        if (this.config.trapFocus)
        {
            setTimeout(() =>
                       {
                           this.trapFocus = true;
                           this.detectChanges();
                       });
        }

        if (this.overlay.backdropElement)
        {
            this.subscribeToObservable(fromEvent(this.overlay.backdropElement, "contextmenu"), (event) =>
            {
                if (this.m_config.dismissOnRightClick)
                {
                    //cancel the event
                    event.preventDefault();
                    event.stopPropagation();

                    // close overlay
                    this.closeOverlay();
                }
            });
        }

        this.opened.emit();
    }

    private detachOverlay(animate = true)
    {
        if (this.overlay?.hasAttached())
        {
            if (animate && this.m_config.isAnimationsEnabled && this.animationState !== "leave")
            {
                // Once animation is finished we'll detach
                this.animationState = "leave";
            }
            else
            {
                this.trapFocus = false;
                if (this.m_onTopMaintainer)
                {
                    this.m_onTopMaintainer?.disconnect();
                    this.m_onTopMaintainer = null;
                    if (this.config?.showOnTop) 
                    {
                        OverlayComponent.numTopLevelOverlays--;
                    }
                }
                this.overlay.detach();
                this.closed.emit();
                this.animationState = "void";
                this.overlay.dispose();
                this.overlay = null;
            }

            this.markForCheck();
        }
    }

    private createOverlay()
    {
        this.overlay   = this.overlayService.create(this.cdkConfig);
        this.overlayId = this.getOverlayId();

        this.subscribeToObservable(this.overlay.keydownEvents(), (event: KeyboardEvent) =>
        {
            if (this.config.keyboardCloseable && event.key === "Escape") this.closeOverlay();
        });

        this.subscribeToObservable(this.overlay.backdropClick(), (event: MouseEvent) =>
        {
            if (this.m_config.closableViaBackdrop) this.closeOverlay();
        });
    }

    private destroyOverlay()
    {
        this.detachOverlay(false);

        this.closed.complete();
        this.opened.complete();
    }
}

export class OverlayConfig
{
    private static id = 0;

    public static newInstance(config?: Partial<OverlayConfig>): OverlayConfig
    {
        return new OverlayConfig().adoptConfig(config);
    }

    public static onTopDraggable(config?: Partial<OverlayConfig>): OverlayConfig
    {
        return new OverlayConfig().onTopDraggable()
                                  .adoptConfig(config);
    }

    public static rightSidebar(overlay: Overlay,
                               config?: Partial<OverlayConfig>): OverlayConfig
    {
        return new OverlayConfig().rightSidebar(overlay)
                                  .adoptConfig(config);
    }

    public static dialog(config?: Partial<OverlayConfig>): OverlayConfig
    {
        return new OverlayConfig().dialogDefaults()
                                  .adoptConfig(config);
    }

    public static dropdown(config?: Partial<OverlayConfig>): OverlayConfig
    {
        return new OverlayConfig().dropdownDefaults()
                                  .adoptConfig(config);
    }

    public static wizard(config?: Partial<OverlayConfig>): OverlayConfig
    {
        return new OverlayConfig().wizardDefaults()
                                  .adoptConfig(config);
    }

    public static importExport(config?: Partial<OverlayConfig>): OverlayConfig
    {
        return new OverlayConfig().importExportDefaults()
                                  .adoptConfig(config);
    }

    public readonly id = `overlay-${OverlayConfig.id++}`;

    panelClass: string;

    showBackdrop: boolean;

    keyboardCloseable: boolean = true;

    closableViaBackdrop: boolean = true;

    showCloseButton: boolean;

    showOnTop: boolean;

    isAnimationsEnabled: boolean;

    dialogAnimationTranslateY: string = "25%";

    slideFromRight: boolean = false;

    isDraggable: boolean;

    coverAnchorWhenDisplayed: boolean;

    anchorPositions: ConnectedPosition[];

    // applies to dropdown only
    dismissOnRightClick: boolean;

    overlayClass: string;

    width: number | string;

    minWidth: number | string;

    maxWidth: number | string = "90vw";

    height: number | string;

    minHeight: number | string;

    maxHeight: number | string;

    borderRadius: number = 16;

    positionStrategy: PositionStrategy;

    originOffsetX: number;

    originOffsetY: number;

    scrollStrategy: ScrollStrategy;

    backdropClass: string;

    trapFocus: boolean = true;

    m_containerClasses: string[] = [];
    set containerClasses(classes: string[])
    {
        if (classes?.length) this.m_containerClasses.push(...classes);
    }

    get containerClasses(): string[]
    {
        return this.m_containerClasses;
    }

    constructor()
    {
        this.dialogDefaults();
    }

    private adoptConfig(config: Partial<OverlayConfig>): OverlayConfig
    {
        Object.assign(this, config || {});
        return this;
    }

    onTopDraggable(): OverlayConfig
    {
        this.showCloseButton = true;
        this.isDraggable     = true;
        this.m_containerClasses.push("on-top-grip");

        return this;
    }

    rightSidebar(overlay: Overlay): OverlayConfig
    {
        this.height              = "100%";
        this.showCloseButton     = true;
        this.showBackdrop        = false;
        this.closableViaBackdrop = false;
        this.borderRadius        = 0;
        this.width               = 420;
        this.positionStrategy    = overlay.position()
                                          .global()
                                          .top()
                                          .right();
        this.trapFocus           = false;
        this.slideFromRight      = true;

        return this;
    }

    dialogDefaults(): OverlayConfig
    {
        this.showBackdrop             = true;
        this.showCloseButton          = false;
        this.isAnimationsEnabled      = true;
        this.isDraggable              = false;
        this.coverAnchorWhenDisplayed = false;
        this.overlayClass             = "";

        return this;
    }

    dropdownDefaults(): OverlayConfig
    {
        this.showBackdrop             = true;
        this.closableViaBackdrop      = true;
        this.showCloseButton          = false;
        this.isAnimationsEnabled      = true;
        this.coverAnchorWhenDisplayed = true;
        this.overlayClass             = "";
        this.backdropClass            = "cdk-overlay-transparent-backdrop";

        return this;
    }

    wizardDefaults(): OverlayConfig
    {
        this.showBackdrop             = true;
        this.showCloseButton          = false;
        this.isAnimationsEnabled      = true;
        this.isDraggable              = false;
        this.coverAnchorWhenDisplayed = false;
        this.closableViaBackdrop      = false;
        this.overlayClass             = "";
        this.containerClasses         = ["dialog-xl"];

        return this;
    }

    importExportDefaults(): OverlayConfig
    {
        this.showBackdrop             = true;
        this.showCloseButton          = true;
        this.isAnimationsEnabled      = true;
        this.isDraggable              = false;
        this.coverAnchorWhenDisplayed = false;
        this.closableViaBackdrop      = false;
        this.overlayClass             = "";
        this.trapFocus                = false;
        this.containerClasses         = ["dialog-sm"];

        return this;
    }
}
